'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { usePDF } from '@react-pdf/renderer'
import { Button, Loader } from '@skop-app/lib-front'

import {
  type CatalogPdfDocumentElement,
  useCatalogPdfDocumentState,
} from '@/components/organisms/Viewer'

const PDF_GENERATION_DELAY_MS = 45_000
const CATALOG_PDF_FILENAME = 'catalogue-skop.pdf'

const triggerDownload = (url: string, fileName = CATALOG_PDF_FILENAME) => {
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
}

type CatalogPreviewProps = {
  data: any
  template?: string
  date?: string
  firstPageNumber?: number
  rotorFooterLabel?: string
  locale?: string
  supportedLocales?: string[]
  defaultLocale?: string
  projectDefaultLocale?: string
  fileName?: string
  previewTitleKey?: string
}

export const CatalogPreview = ({
  data,
  template,
  date,
  firstPageNumber,
  rotorFooterLabel,
  locale,
  supportedLocales,
  defaultLocale,
  projectDefaultLocale,
  fileName,
  previewTitleKey,
}: CatalogPreviewProps) => {
  const { document, isReady } = useCatalogPdfDocumentState({
    data,
    template,
    date,
    firstPageNumber,
    rotorFooterLabel,
    locale,
    supportedLocales,
    defaultLocale,
    projectDefaultLocale,
  })

  if (!isReady || !document) {
    return <CatalogPreviewLoading previewTitleKey={previewTitleKey} />
  }

  return (
    <CatalogPreviewDocument
      document={document}
      fileName={fileName}
      previewTitleKey={previewTitleKey}
    />
  )
}

const resolveCatalogPreviewTitle = (
  t: ReturnType<typeof useTranslations>,
  previewTitleKey?: string,
) => t(previewTitleKey || 'Catalog.previewTitle')

const CatalogPreviewLoading = ({
  previewTitleKey,
}: {
  previewTitleKey?: string
}) => {
  const t = useTranslations()
  const previewTitle = resolveCatalogPreviewTitle(t, previewTitleKey)

  return (
    <div className="flex h-full flex-col">
      <div className="font-heading flex items-center justify-between gap-3 rounded-t-md border-b border-zinc-200 bg-zinc-100 p-4 text-lg leading-none text-slate-500">
        <span>{previewTitle}</span>
      </div>
      <div className="relative min-h-0 flex-1 overflow-hidden">
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white">
          <Loader />
          <p className="text-sm text-neutral-500">
            {t('Catalog.initializing')}
          </p>
        </div>
      </div>
    </div>
  )
}

type CatalogPreviewDocumentProps = {
  document: CatalogPdfDocumentElement
  fileName?: string
  previewTitleKey?: string
}

const CatalogPreviewDocument = ({
  document,
  fileName,
  previewTitleKey,
}: CatalogPreviewDocumentProps) => {
  const t = useTranslations()
  const [delayedDocument, setDelayedDocument] =
    useState<CatalogPdfDocumentElement | null>(null)
  const didMountRef = useRef(false)
  const previewTitle = resolveCatalogPreviewTitle(t, previewTitleKey)

  const [instance, updateInstance] = usePDF({ document })

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      return
    }

    updateInstance(document)
  }, [document, updateInstance])

  useEffect(() => {
    if (!instance.loading) return

    const timeout = window.setTimeout(() => {
      setDelayedDocument(document)
    }, PDF_GENERATION_DELAY_MS)

    return () => window.clearTimeout(timeout)
  }, [document, instance.loading])

  const retryGeneration = useCallback(() => {
    setDelayedDocument(null)
    updateInstance(document)
  }, [document, updateInstance])

  const downloadPdf = useCallback(() => {
    if (!instance.url) return
    triggerDownload(instance.url, fileName)
  }, [fileName, instance.url])

  const hasError = Boolean(instance.error)
  const previewUrl =
    !instance.loading && !hasError && instance.url
      ? `${instance.url}#toolbar=1`
      : undefined
  const showDelayedMessage =
    delayedDocument === document && !previewUrl && !hasError

  return (
    <div className="flex h-full flex-col">
      <div className="font-heading flex items-center justify-between gap-3 rounded-t-md border-b border-zinc-200 bg-zinc-100 p-4 text-lg leading-none text-slate-500">
        <span>{previewTitle}</span>
        {previewUrl ? (
          <Button variant="secondary" onClick={downloadPdf}>
            {t('Common.download-pdf')}
          </Button>
        ) : null}
      </div>
      <div className="relative min-h-0 flex-1 overflow-hidden">
        {previewUrl && !hasError ? (
          <iframe
            title={previewTitle}
            src={previewUrl}
            className="h-full w-full border-0"
          />
        ) : null}

        {instance.loading && !previewUrl ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white">
            <Loader />
            <p className="text-sm text-neutral-500">
              {t('Catalog.pdfGenerating')}
            </p>
          </div>
        ) : null}

        {showDelayedMessage ? (
          <div className="absolute inset-x-6 top-1/2 -translate-y-1/2 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            <p>{t('Catalog.pdfGenerationDelayed')}</p>
            <Button
              variant="secondary"
              className="mt-3"
              onClick={retryGeneration}
            >
              {t('Common.retry')}
            </Button>
          </div>
        ) : null}

        {hasError ? (
          <div className="absolute inset-x-6 top-1/2 -translate-y-1/2 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <p>{t('Catalog.pdfGenerationFailed')}</p>
            <Button
              variant="secondary"
              className="mt-3"
              onClick={retryGeneration}
            >
              {t('Common.retry')}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
